import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import AvatarMedia from "@/components/avatar/AvatarMedia";
import GuidedCalendarSheet from "@/components/calendar/GuidedCalendarSheet";
import { ErrorState } from "@/components/shared/StateViews";
import { useAuth } from "@/contexts/AuthContext";
import { secondaryLine, avatarFooterLine } from "@/lib/avatarUi";
import { friendlyError } from "@/lib/errors";
import { CheckCircle2, ArrowLeft, Pencil, Check, X, Quote } from "lucide-react";
import { toast } from "sonner";

// Avatar review as a PROFILE (§9.9): hero portrait + identity, narrative sections
// (Dores & Medos / Desejos / Objeções / Comportamento & Linguagem), the strategic
// matrix as three columns of chips, and a fixed "Confirmar avatar" action bar.
// Every text field is editable inline (tap → input → PATCH /avatars/:id).
export default function AvatarReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [avatar, setAvatar] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [guidedOpen, setGuidedOpen] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const { data } = await api.get(`/avatars/${id}`);
      setAvatar(data);
    } catch (e) {
      setLoadError(friendlyError(e));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveField = async (field, value) => {
    const { data } = await api.patch(`/avatars/${id}`, { [field]: value });
    setAvatar((a) => ({ ...(a || {}), ...(data || {}), [field]: value }));
  };

  const complete = async () => {
    setSaving(true);
    try {
      await api.post(`/avatars/${id}/complete`);
      await refresh();
      const confetti = (await import("canvas-confetti")).default;
      confetti({ particleCount: 180, spread: 80, origin: { y: 0.6 }, colors: ["#A16BF7", "#D6C2FD", "#ffffff"] });
      toast.success("Avatar finalizado! Gerando o retrato IA... ✦");
      setGuidedOpen(true);
    } catch (err) {
      if (err.response?.status === 422) {
        const missing = err.response.data?.detail?.missing_fields || [];
        toast.error(missing.length ? "Faltam campos: " + missing.join(", ") : friendlyError(err));
      } else {
        toast.error(friendlyError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-brand-bg" data-testid="avatar-review-page">
        <AppHeader />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
          <ErrorState message={loadError} onRetry={load} testid="avatar-review-error" />
        </div>
      </div>
    );
  }

  if (!avatar) return null;
  const matriz = avatar.matriz_estrategica || {};
  const meta = secondaryLine(avatar);
  const footer = avatarFooterLine(avatar);

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="avatar-review-page">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-8 pb-32">
        <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-ui mb-5">
          Perfil do avatar · revisão
        </div>

        {/* Hero */}
        <div className="flex items-center gap-5 mb-8">
          <AvatarMedia
            avatar={avatar}
            aspectClass="aspect-square"
            roundedClass="rounded-3xl"
            monogramClass="text-4xl"
            className="w-24 sm:w-28 shrink-0 border border-brand-rose/40"
          />
          <div className="min-w-0 flex-1">
            <InlineText
              value={avatar.nome}
              field="nome"
              placeholder="Nome do avatar"
              onSave={saveField}
              testid="review-field-nome"
              className="font-display text-3xl sm:text-4xl text-brand-ink"
            />
            <div className="flex items-center gap-2 mt-1">
              {meta && <span className="text-[13.5px] text-brand-ink2 font-ui">{meta}</span>}
              <span className="shrink-0 w-5 h-5 rounded-full bg-brand-rose flex items-center justify-center" title="Avatar em revisão">
                <Check className="w-3 h-3 text-white" />
              </span>
            </div>
            {footer && <div className="text-[12.5px] text-brand-ink3 font-ui mt-0.5">{footer}</div>}
          </div>
        </div>

        {/* Bio / context */}
        <Section title="Contexto & bio">
          {avatar.resumo_card && (
            <p className="text-[14.5px] text-brand-ink2 leading-relaxed mb-3" data-testid="review-bio">
              {avatar.resumo_card}
            </p>
          )}
          <EditableField label="Contexto de vida" field="contexto_vida" value={avatar.contexto_vida} onSave={saveField} multiline testid="review-field-contexto_vida" />
        </Section>

        {/* Identity */}
        <Section title="Identidade">
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
            <EditableField label="Faixa etária" field="faixa_etaria" value={avatar.faixa_etaria} onSave={saveField} testid="review-field-faixa_etaria" />
            <EditableField label="Ocupação" field="ocupacao" value={avatar.ocupacao} onSave={saveField} testid="review-field-ocupacao" />
            <EditableField label="Nicho" field="nicho" value={avatar.nicho} onSave={saveField} testid="review-field-nicho" />
            <EditableField label="Localização" field="localizacao" value={avatar.localizacao} onSave={saveField} testid="review-field-localizacao" />
          </div>
        </Section>

        {/* Narrative */}
        <Section title="Dores & Medos">
          <EditableField label="Dores" field="dores" value={avatar.dores} onSave={saveField} multiline testid="review-field-dores" />
          <EditableField label="Frustrações" field="frustracoes" value={avatar.frustracoes} onSave={saveField} multiline testid="review-field-frustracoes" />
          <EditableField label="Medos" field="medos" value={avatar.medos} onSave={saveField} multiline testid="review-field-medos" />
        </Section>

        <Section title="Desejos">
          <EditableField label="O que ela quer" field="desejos" value={avatar.desejos} onSave={saveField} multiline testid="review-field-desejos" />
        </Section>

        <Section title="Objeções">
          <EditableField label="O que a trava" field="objecoes" value={avatar.objecoes} onSave={saveField} multiline testid="review-field-objecoes" />
        </Section>

        <Section title="Comportamento & Linguagem">
          <EditableField label="Comportamento" field="comportamento" value={avatar.comportamento} onSave={saveField} multiline testid="review-field-comportamento" />
          <EditableField
            label="Linguagem real"
            field="linguagem_real"
            value={avatar.linguagem_real}
            onSave={saveField}
            multiline
            quote
            testid="review-field-linguagem_real"
          />
        </Section>

        {/* Strategic matrix */}
        <div className="mt-8 bg-brand-surface border border-brand-line rounded-3xl p-5 sm:p-7" data-testid="review-matriz">
          <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-ui mb-1">
            Matriz Estratégica
          </div>
          <h2 className="font-display text-2xl sm:text-3xl text-brand-ink mb-5">
            10 dores · 10 desejos · 10 objeções
          </h2>
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <ChipColumn title="Dores" items={matriz.dores} />
            <ChipColumn title="Desejos" items={matriz.desejos} />
            <ChipColumn title="Objeções" items={matriz.objecoes} />
          </div>
        </div>
      </div>

      {/* Fixed action bar (above the mobile safe area). */}
      <div
        className="fixed bottom-0 inset-x-0 z-40 bg-brand-bg/95 backdrop-blur-xl border-t border-brand-line"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="back-to-builder"
            onClick={() => nav(`/avatars/${id}/builder`)}
            className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full border border-brand-line text-brand-ink2 hover:border-brand-rose hover:text-brand-ink font-ui text-[13px] transition-colors min-h-[44px]"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Voltar e ajustar</span>
          </button>
          <button
            data-testid="complete-avatar"
            onClick={complete}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-6 py-3 rounded-full font-ui text-[14px] tracking-wide disabled:opacity-60 transition-colors min-h-[44px]"
          >
            <CheckCircle2 className="w-4 h-4" />
            {saving ? "Finalizando..." : "Confirmar avatar"}
          </button>
        </div>
      </div>

      <GuidedCalendarSheet
        open={guidedOpen}
        onOpenChange={(o) => { if (!o) { setGuidedOpen(false); nav("/avatars"); } }}
        avatarId={id}
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-6 bg-brand-surface border border-brand-line rounded-3xl p-5 sm:p-6">
      <h3 className="font-display text-lg sm:text-xl text-brand-ink mb-3">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

// Editable label + value. Tap to edit; Enter (single-line) or the ✓ button saves,
// Esc / ✗ cancels. Blur also commits so it feels like a document.
function EditableField({ label, field, value, onSave, multiline = false, quote = false, placeholder = "Toque para preencher", testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const skipBlur = useRef(false);

  useEffect(() => { if (!editing) setDraft(value || ""); }, [value, editing]);

  const commit = async () => {
    if (skipBlur.current) { skipBlur.current = false; return; }
    if ((draft || "") === (value || "")) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(field, draft);
      setEditing(false);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { skipBlur.current = true; setDraft(value || ""); setEditing(false); };

  if (editing) {
    return (
      <div data-testid={testid} data-editing="true">
        <FieldLabel>{label}</FieldLabel>
        <div className="flex items-start gap-2">
          {multiline ? (
            <textarea
              data-testid={`${testid}-input`}
              autoFocus
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              className="flex-1 px-3 py-2 rounded-xl bg-brand-elevated border border-brand-rose/60 focus:border-brand-rose focus:outline-none resize-none text-[16px] text-white placeholder:text-brand-ink3"
            />
          ) : (
            <input
              data-testid={`${testid}-input`}
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") cancel(); }}
              className="flex-1 px-3 py-2 rounded-xl bg-brand-elevated border border-brand-rose/60 focus:border-brand-rose focus:outline-none text-[16px] text-white placeholder:text-brand-ink3"
            />
          )}
          <div className="flex flex-col gap-1 shrink-0">
            <button
              type="button"
              data-testid={`${testid}-save`}
              onMouseDown={() => { skipBlur.current = true; }}
              onClick={commit}
              disabled={saving}
              aria-label="Salvar"
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-brand-rose text-white disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid={`${testid}-cancel`}
              onMouseDown={cancel}
              aria-label="Cancelar"
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-brand-elevated border border-brand-line text-brand-ink2"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasValue = Boolean(value && String(value).trim());

  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => setEditing(true)}
      className="group w-full text-left rounded-xl -mx-2 px-2 py-1.5 hover:bg-brand-elevated/40 transition-colors"
    >
      <FieldLabel>
        {label}
        <Pencil className="inline w-3 h-3 ml-1.5 text-brand-ink3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </FieldLabel>
      {quote && hasValue ? (
        <blockquote className="relative pl-6 text-[14.5px] text-brand-ink italic leading-relaxed">
          <Quote className="absolute left-0 top-0.5 w-4 h-4 text-brand-rose" />
          {value}
        </blockquote>
      ) : (
        <div className={`text-[14.5px] leading-relaxed ${hasValue ? "text-brand-ink" : "text-brand-sand italic"}`}>
          {hasValue ? value : placeholder}
        </div>
      )}
    </button>
  );
}

// Inline editable heading (used for the hero name). Larger typography, no label.
function InlineText({ value, field, onSave, placeholder, testid, className = "" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const skipBlur = useRef(false);

  useEffect(() => { if (!editing) setDraft(value || ""); }, [value, editing]);

  const commit = async () => {
    if (skipBlur.current) { skipBlur.current = false; return; }
    if ((draft || "") === (value || "")) { setEditing(false); return; }
    try { await onSave(field, draft); } catch (err) { toast.error(friendlyError(err)); }
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        data-testid={`${testid}-input`}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") { skipBlur.current = true; setDraft(value || ""); setEditing(false); } }}
        className={`w-full bg-transparent border-b border-brand-rose/60 focus:outline-none ${className}`}
      />
    );
  }

  return (
    <button type="button" data-testid={testid} onClick={() => setEditing(true)} className={`text-left inline-flex items-center gap-2 group ${className}`}>
      <span className={value ? "" : "text-brand-sand italic"}>{value || placeholder}</span>
      <Pencil className="w-4 h-4 text-brand-ink3 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

function FieldLabel({ children }) {
  return (
    <div className="text-[10.5px] tracking-[0.2em] uppercase text-brand-ink2 font-ui mb-1">
      {children}
    </div>
  );
}

function ChipColumn({ title, items }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="min-w-0">
      <div className="font-ui text-[11px] tracking-[0.16em] uppercase text-brand-rose mb-2">
        {title} ({list.length})
      </div>
      <div className="max-h-64 overflow-y-auto overflow-x-hidden scrollbar-soft space-y-1.5 pr-1">
        {list.map((it, i) => (
          <span
            key={i}
            className="block rounded-lg bg-brand-elevated border border-brand-line px-2.5 py-1.5 text-[11.5px] text-brand-ink2 leading-snug"
          >
            {it}
          </span>
        ))}
        {list.length === 0 && (
          <span className="block text-[12px] text-brand-sand italic">—</span>
        )}
      </div>
    </div>
  );
}
